/**
 * ISSUE-1382. The Last play card at the top of Matchup while a game is live,
 * under the live state panel: the newest play's words, its clock and period
 * ("10:19 · 1st Quarter") and the team's mark, as v1's gamecast showed it.
 * The server picks the play (`lastPlay` on /event/detail-get) and sends it
 * only while the game is in progress, so it refreshes on the detail's own
 * live poll and a final never shows a stale play.
 */
import type { ReactNode } from "react";

import { TeamDisc } from "../scores/TeamDisc";
import { GameSectionCard } from "./GameSectionCard";

export const LAST_PLAY_TITLE = "Last play";

export type typeLastPlayWire = {
  entryId: string;
  description: string;
  periodLabel?: string | null;
  clockLabel?: string | null;
  teamId?: string | null;
  isScoringPlay: boolean;
  awayScore?: number | null;
  homeScore?: number | null;
};

type typeLastPlayTeam = {
  teamId: string;
  abbreviation: string;
  plateText: string;
  logoUrl: string | null;
};

/** "10:19 · 1st Quarter", whichever parts the server sent; "" for neither. */
export function lastPlayWhenLabel(play: {
  clockLabel?: string | null;
  periodLabel?: string | null;
}): string {
  return [play.clockLabel ?? "", play.periodLabel ?? ""]
    .map((part) => part.trim())
    .filter((part) => part !== "")
    .join(" · ");
}

export const LastPlayCard = ({
  play,
  away,
  home,
}: {
  play: typeLastPlayWire;
  away: typeLastPlayTeam;
  home: typeLastPlayTeam;
}): ReactNode => {
  const team =
    play.teamId == null
      ? null
      : play.teamId === away.teamId
        ? away
        : play.teamId === home.teamId
          ? home
          : null;
  const when = lastPlayWhenLabel(play);
  const hasScore =
    play.isScoringPlay &&
    typeof play.awayScore === "number" &&
    typeof play.homeScore === "number";
  return (
    <GameSectionCard
      testId="last-play-card"
      title={LAST_PLAY_TITLE}
      caption={when === "" ? undefined : when}
    >
      <div className="flex items-start gap-3">
        {team !== null ? (
          <TeamDisc
            plateText={team.plateText}
            logoUrl={team.logoUrl}
            size="mini"
            testId="last-play-mark"
          />
        ) : null}
        <p
          className={
            play.isScoringPlay
              ? "min-w-0 flex-1 text-body-strong text-ink"
              : "min-w-0 flex-1 text-body text-ink"
          }
          data-testid="last-play-description"
        >
          {team !== null ? (
            <span className="sr-only">{team.abbreviation}: </span>
          ) : null}
          {play.description}
          {play.isScoringPlay ? (
            <span className="sr-only"> Scoring play.</span>
          ) : null}
        </p>
        {hasScore ? (
          <span
            className="shrink-0 text-body-strong text-ink tabular-nums"
            data-testid="last-play-score"
          >
            {play.awayScore}-{play.homeScore}
          </span>
        ) : null}
      </div>
    </GameSectionCard>
  );
};
